import axiosInstance from '../axiosInstance';
import axios from 'axios'; // 기본 axios 인스턴스
import { COMMON_HEADERS, ROUTES } from './apiURLs';
import { getCookie } from '../../utils/utilsCookies';

// axios 인스턴스 매핑
const axiosInstances = {
    'axios': axios, // 기본 axios
    'axiosInstance': axiosInstance // 커스텀 axios 인스턴스
};

// 상위 경로들을 찾아서 합치는 함수
const buildFullPath = (route) => {
    // ROUTES 객체에서 해당 route 객체의 위치를 찾아서 상위 경로들을 합치기
    const findRoutePath = (routes, targetRoute, currentPath = '') => {
        for (const value of Object.values(routes)) {
            const result = processRouteValue(value, targetRoute, currentPath);
            if (result !== null) {
                return result;
            }
        }
        return null;
    };

    // 라우트 값을 처리하는 헬퍼 함수
    const processRouteValue = (value, targetRoute, currentPath) => {
        if (typeof value !== 'object' || value === null) {
            return null;
        }

        const newPath = buildNewPath(value.path, currentPath);

        // 현재 객체가 targetRoute와 동일한지 확인 (객체 참조 비교)
        if (value === targetRoute) {
            return newPath;
        }

        // 재귀적으로 하위 객체 검색
        return findRoutePath(value, targetRoute, newPath);
    };

    // 새로운 경로를 생성하는 헬퍼 함수
    const buildNewPath = (valuePath, currentPath) => {
        if (!valuePath) {
            return currentPath;
        }

        let newPath;
        if (currentPath) {
            newPath = valuePath.startsWith('/') 
                ? `${currentPath}${valuePath}`
                : `${currentPath}/${valuePath}`;
        } else {
            newPath = valuePath;
        }

        // 끝의 '/' 제거 (ReDoS 방지를 위해 안전한 방식 사용)
        return removeTrailingSlashes(newPath);
    };

    // 끝의 슬래시를 제거하는 헬퍼 함수
    const removeTrailingSlashes = (path) => {
        let result = path;
        while (result.endsWith('/')) {
            result = result.slice(0, -1);
        }
        return result;
    };
    
    // ROUTES에서 해당 route 객체의 위치를 찾기
    const fullPath = findRoutePath(ROUTES, route);
    
    // 만약 찾지 못했다면, route 객체 자체의 path만 사용
    if (!fullPath && route.path) {
        return removeTrailingSlashes(route.path);
    }
    
    return fullPath || '';
};

// 기본 HTTP 메서드 함수들
export const apiUtils = {
    // GET 요청
    get: async (url, params = {}, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const config = { params, headers };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.get(url, config);
            return resp.data;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'GET 요청 처리 중 오류가 발생했습니다.'));
        }
    },

    // POST 요청
    post: async (url, data = {}, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const config = { headers };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.post(url, data, config);
            return resp.data;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'POST 요청 처리 중 오류가 발생했습니다.'));
        }
    },

    // PUT 요청
    put: async (url, data = {}, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const config = { headers };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.put(url, data, config);
            return resp.data;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'PUT 요청 처리 중 오류가 발생했습니다.'));
        }
    },

    // DELETE 요청
    delete: async (url, data = {}, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const config = { data, headers };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.delete(url, config);
            return resp.data;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'DELETE 요청 처리 중 오류가 발생했습니다.'));
        }
    },

    // 파일 다운로드용 GET 요청 (blob 응답)
    getBlob: async (url, params = {}, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const config = {
                params,
                responseType: 'blob',
                headers
            };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.get(url, config);
            return resp;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'Blob 파일 다운로드 중 오류가 발생했습니다.'));
        }
    },

    // multipart/form-data POST 요청
    postMultipart: async (url, data, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const finalHeaders = { ...COMMON_HEADERS.MULTIPART, ...headers };
            const config = { headers: finalHeaders };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.post(url, data, config);
            return resp.data;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'Multipart POST 요청 처리 중 오류가 발생했습니다.'));
        }
    },

    // multipart/form-data PUT 요청
    putMultipart: async (url, data, instance = axiosInstance, headers = {}, withCredentials = false) => {
        try {
            const finalHeaders = { ...COMMON_HEADERS.MULTIPART, ...headers };
            const config = { headers: finalHeaders };
            if (withCredentials) {
                config.withCredentials = true;
            }
            const resp = await instance.put(url, data, config);
            return resp.data;
        } catch (error) {
            return Promise.reject(error instanceof Error ? error : new Error(error.message || 'Multipart PUT 요청 처리 중 오류가 발생했습니다.'));
        }
    }
};

// APIS 객체와 함께 사용할 수 있는 헬퍼 함수
export const callAPI = (route, method, data = {}, params = {}) => {
    const { path, dynamic, template, params: requiredParams, axiosInstance: instanceType, headers: apiHeaders, responseType: routeResponseType } = route;
    
    // 경로 구성 - 상위 경로들을 찾아서 합치기
    let url = buildFullPath(route);
    
    // 동적 경로 처리
    if (dynamic && requiredParams && requiredParams.length > 0) {
        // template이 있으면 전체 경로 + template 사용, 없으면 전체 경로의 마지막 부분을 template로 사용
        if (template) {
            // 상위 경로에서 마지막 path 부분을 template로 교체
            const pathParts = url.split('/');
            pathParts[pathParts.length - 1] = template;
            url = pathParts.join('/');
        }
        
        requiredParams.forEach(param => {
            if (params[param]) {
                url = url.replace(`{${param}}`, params[param]);
            }
        });
    }
    
    // URL 끝에 / 추가 (이미 /로 끝나는 경우 제외)
    if (url && !url.endsWith('/')) {
        url = url + '/';
    }
    
    // axios 인스턴스 선택
    const instance = axiosInstances[instanceType] || axiosInstance;
    
    // 헤더 처리 - apiHeaders가 undefined일 수 있음
    const finalHeaders = { ...(apiHeaders || {}) };
    const withCredentials = (apiHeaders && apiHeaders.withCredentials) || false;
    
    // csrftoken이 'csrftoken' 문자열인 경우 실제 쿠키 값으로 교체
    if (finalHeaders['X-CSRFToken'] === 'csrftoken') {
        finalHeaders['X-CSRFToken'] = getCookie('csrftoken');
    }
    
    // responseType 처리 (route 객체에서 직접 가져오기)
    const responseType = routeResponseType;
    
    // withCredentials가 true인 경우 헤더에서 제거 (별도 파라미터로 전달)
    if (withCredentials) {
        delete finalHeaders.withCredentials;
    }
    
    // FormData인 경우 Content-Type을 명시적으로 제거 (브라우저가 boundary를 추가하도록)
    if (data instanceof FormData) {
        // Content-Type 헤더를 제거하여 브라우저가 자동으로 boundary를 추가하도록 함
        delete finalHeaders['Content-Type'];
    }
    
    // HTTP 메서드별 호출
    switch (method) {
        case 'GET':
            // responseType이 'blob'인 경우 특별 처리
            if (responseType === 'blob') {
                // 동적 경로인 경우, requiredParams에 포함된 파라미터는 URL 경로에 사용되고
                // 나머지 params는 쿼리 파라미터로 전달되어야 함
                let queryParams = params;
                if (dynamic && requiredParams && requiredParams.length > 0) {
                    // requiredParams에 포함되지 않은 파라미터만 쿼리 파라미터로 전달
                    queryParams = {};
                    Object.keys(params).forEach(key => {
                        if (!requiredParams.includes(key)) {
                            queryParams[key] = params[key];
                        }
                    });
                }
                return apiUtils.getBlob(url, queryParams, instance, finalHeaders, withCredentials);
            }
            // 동적 경로인 경우, requiredParams에 포함된 파라미터는 URL 경로에 사용되고
            // 나머지 params는 쿼리 파라미터로 전달되어야 함
            let queryParams = params;
            if (dynamic && requiredParams && requiredParams.length > 0) {
                // requiredParams에 포함되지 않은 파라미터만 쿼리 파라미터로 전달
                queryParams = {};
                Object.keys(params).forEach(key => {
                    if (!requiredParams.includes(key)) {
                        queryParams[key] = params[key];
                    }
                });
            }
            return apiUtils.get(url, queryParams, instance, finalHeaders, withCredentials);
        case 'POST':
            return apiUtils.post(url, data, instance, finalHeaders, withCredentials);
        case 'PUT':
            return apiUtils.put(url, data, instance, finalHeaders, withCredentials);
        case 'DELETE':
            return apiUtils.delete(url, data, instance, finalHeaders, withCredentials);
        default:
            throw new Error(`Unsupported HTTP method: ${method}`);
    }
};
